import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { Category, CategoryConstructorArgs } from './Category'
import { Contract, ContractConstructorArgs } from './Contract'
import { Field, FieldConstructorArgs } from './Field'
import { Industry } from './Industry'
import { Item, ItemConstructorArgs } from './Item'
import { Option } from './Option'
import { FKType } from './SQLBuilder'
import { Type, TypeConstructorArgs } from './Type'
import { User } from './User'

type EditionConstructorArgs = Omit<
    ConstructorArgs<() => Edition>,
    | 'pitch'
    | 'categories'
    | 'services'
    | 'products'
    | 'packages'
    | 'classes'
    | 'subscriptions'
    | 'gifts'
    | 'reservations'
> & {
    pitch: Option[] | string[] | null
    categories: Category[] | CategoryConstructorArgs[] | null
    services: Item[] | ItemConstructorArgs[] | null
    products: Item[] | ItemConstructorArgs[] | null
    packages: Item[] | ItemConstructorArgs[] | null
    classes: Item[] | ItemConstructorArgs[] | null
    subscriptions: Item[] | ItemConstructorArgs[] | null
    gifts: Item[] | ItemConstructorArgs[] | null
    reservations: Item[] | ItemConstructorArgs[] | null
    contracts: Contract[] | ContractConstructorArgs[] | null
    forms: Type[] | TypeConstructorArgs[] | null
    intake: Field[] | FieldConstructorArgs[] | null
}

export class Edition extends BaseRecord {
    readonly recordType = 'edition'

    name?: string
    allowBasicPlan?: boolean
    videoUri?: string
    imageUri?: string
    squareImageUri?: string
    contentImageUri?: string
    color?: string
    backgroundColor?: string
    bio?: string
    cancelledDate?: moment.Moment
    lastModifiedDate?: moment.Moment
    readonly teamPrice?: number
    readonly suitePrice?: number
    readonly premiumPrice?: number
    readonly showImageBorder: boolean = false
    readonly prefix?: string
    readonly features: string[] = []
    readonly basicFeatures: string[] = []
    readonly pitch: Option[] = []
    readonly defaultCategories: Category[] = []
    readonly defaultServices: Item[] = []
    readonly defaultProducts: Item[] = []
    readonly defaultPackages: Item[] = []
    readonly defaultClasses: Item[] = []
    readonly defaultSubscriptions: Item[] = []
    readonly defaultGifts: Item[] = []
    readonly defaultReservations: Item[] = []
    readonly defaultContracts: Contract[] = []
    readonly defaultTypes: Type[] = []
    readonly defaultIntakeFields: Field[] = []

    constructor(data?: Partial<EditionConstructorArgs>) {
        super(data)
        if (!data) {
            this.owner = this.creator = PSApplication.me()
            return
        }

        this.name = data.name ?? undefined
        this.videoUri = data.video_uri ?? undefined
        this.imageUri = data.image_uri ?? undefined
        this.squareImageUri = data.square_image_uri ?? undefined
        this.contentImageUri = data.content_image_uri ?? undefined
        this.color = data.color ?? undefined
        this.backgroundColor = data.background_color ?? undefined
        this.bio = data.bio ?? undefined
        this.allowBasicPlan = data.allow_basic_plan ?? false
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
        this.teamPrice = this.propertyFromCurrencyArg(data.team_price)
        this.suitePrice = this.propertyFromCurrencyArg(data.suite_price)
        this.premiumPrice = this.propertyFromCurrencyArg(data.premium_price)
        this.showImageBorder = data.show_image_border ?? false
        this.prefix = data.prefix ?? undefined
        this.features = (data.features ?? []).map(String)
        this.basicFeatures = (data.basic_features ?? []).map(String)

        this.pitch = (data.pitch ?? []).map((pitch) =>
            pitch instanceof Option
                ? pitch
                : new Option(undefined, { id: '', label: String(pitch) })
        )
        this.defaultCategories = (data.categories ?? []).map((category) =>
            category instanceof Category ? category : new Category(category)
        )
        this.defaultServices = (data.services ?? []).map((data) =>
            data instanceof Item ? data : new Item(data)
        )
        this.defaultProducts = (data.products ?? []).map((data) =>
            data instanceof Item ? data : new Item(data)
        )
        this.defaultPackages = (data.packages ?? []).map((data) =>
            data instanceof Item ? data : new Item(data)
        )
        this.defaultClasses = (data.classes ?? []).map((data) =>
            data instanceof Item ? data : new Item(data)
        )
        this.defaultSubscriptions = (data.subscriptions ?? []).map((data) =>
            data instanceof Item ? data : new Item(data)
        )
        this.defaultGifts = (data.gifts ?? []).map((data) =>
            data instanceof Item ? data : new Item(data)
        )
        this.defaultReservations = (data.reservations ?? []).map((data) =>
            data instanceof Item ? data : new Item(data)
        )
        this.defaultContracts = (data.contracts ?? []).map((data) =>
            data instanceof Contract ? data : new Contract(data)
        )
        this.defaultTypes = (data.forms ?? []).map((data) =>
            data instanceof Type ? data : new Type(data)
        )
        this.defaultIntakeFields = (data.intake ?? []).map((data) =>
            data instanceof Field ? data : new Field(data)
        )
    }

    clone(overrides?: Partial<EditionConstructorArgs>): Edition {
        return new Edition({ ...this.serializeForClone(), ...overrides })
    }

    /**
     * Generate the displayed name for the version of PocketSuite the user is on. This is currently a function
     * of the user's subscription level, as well as the signature or industry edition they are on.
     */
    static displayNameForPlan(plan: string, industry: Industry, edition: Edition): string {
        if (edition.allowBasicPlan && edition.prefix) {
            if (plan === 'premium') return `${edition.prefix} edition`
            if (plan === 'plus') return `${edition.prefix} Team edition`
            return `${edition.prefix} Basic edition`
        }
        else {
            if (edition && plan) return edition.name!
            else if (industry && plan) return `${industry.title} edition`
            else if (plan === 'plus') return 'Team edition'
            else if (plan === 'basic') return 'Basic edition'
            return 'Premium edition'
        }
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.creator) this.creator = BaseRecord.fromSerializer(data.creator, User)
        if (data.cancelledDate) this.cancelledDate = moment(data.cancelledDate)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
    }

    static fk: FKType = {
        users: ['owner', 'creator'],
    }

    static dataType(): DataType {
        return 'editions'
    }

    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
    get creator(): User | undefined {
        return this.getForeignRecord('creator', User)
    }
    set creator(creator: User | undefined) {
        this.setForeignRecord('creator', creator)
    }
    get industry(): Industry | undefined {
        return this.getForeignRecord('industry', Industry)
    }
    set industry(industry: Industry | undefined) {
        this.setForeignRecord('industry', industry)
    }
}
